import React, { useState } from "react";
import { api, setToken } from "@/api";
import { Lock } from "lucide-react";

export const LoginScreen = ({ title, subtitle, requiredRole, onSuccess }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("login"); // login | forgot | sent
  const [notice, setNotice] = useState("");

  const submit = async () => {
    setError("");
    setBusy(true);
    try {
      const res = await api.login(email.trim(), password);
      if (requiredRole && res.user.role !== requiredRole && res.user.role !== "admin") {
        setError(`This login isn't a ${requiredRole} account.`);
        setBusy(false);
        return;
      }
      setToken(res.token);
      onSuccess(res.user);
    } catch (e) {
      setError(e?.response?.data?.detail || "Login failed. Check your email and password.");
      setBusy(false);
    }
  };

  const sendReset = async () => {
    setError("");
    if (!email.trim()) { setError("Enter your email first."); return; }
    setBusy(true);
    try {
      const res = await api.forgotPassword(email.trim(), window.location.origin);
      setNotice(res.message || "If that email has an account, a reset link is on its way.");
      setMode("sent");
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't send the reset email. Try again.");
    }
    setBusy(false);
  };

  if (mode === "sent") {
    return (
      <div className="max-w-md mx-auto p-4 pt-12" data-testid="login-screen">
        <div className="brutal p-6 text-center" data-testid="forgot-sent">
          <h1 className="font-display text-3xl font-black uppercase tracking-tight">Check Your Email</h1>
          <p className="font-display text-gray-600 mt-3">{notice}</p>
          <button data-testid="back-to-login" onClick={() => { setMode("login"); setError(""); setPassword(""); }} className="mt-5 bg-black text-white font-display font-black uppercase tracking-widest px-6 py-3">
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto p-4 pt-12" data-testid="login-screen">
      <div className="flex items-center gap-2 mb-1">
        <Lock size={28} />
        <h1 className="font-display text-4xl font-black uppercase tracking-tight">{mode === "forgot" ? "Reset Password" : title}</h1>
      </div>
      <p className="font-display text-gray-500 uppercase text-xs tracking-widest mb-6">
        {mode === "forgot" ? "Enter your email — we'll send a reset link" : subtitle}
      </p>
      <div className="brutal p-4 space-y-3">
        <div>
          <label className="font-display text-[11px] uppercase tracking-widest text-gray-500 block mb-1">Email</label>
          <input
            data-testid="login-email"
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (mode === "forgot" ? sendReset() : submit())}
            className="w-full brutal-2 p-3 font-display focus:outline-none bg-white"
            placeholder="you@email.com"
          />
        </div>
        {mode === "login" && (
          <div>
            <label className="font-display text-[11px] uppercase tracking-widest text-gray-500 block mb-1">Password</label>
            <input
              data-testid="login-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submit()}
              className="w-full brutal-2 p-3 font-display focus:outline-none bg-white"
              placeholder="••••••••"
            />
          </div>
        )}
        {error && <p className="text-[#E63946] font-bold uppercase text-sm" data-testid="login-error">{error}</p>}
        {mode === "login" ? (
          <>
            <button
              data-testid="login-submit"
              onClick={submit}
              disabled={busy}
              className="w-full bg-black text-white font-display font-black uppercase tracking-widest py-4 active:scale-95 transition-transform disabled:opacity-60"
            >
              {busy ? "Signing in…" : "Log In"}
            </button>
            <button data-testid="forgot-link" onClick={() => { setMode("forgot"); setError(""); }} className="w-full font-display text-xs uppercase tracking-widest underline text-gray-500">
              Forgot password?
            </button>
          </>
        ) : (
          <>
            <button
              data-testid="send-reset-btn"
              onClick={sendReset}
              disabled={busy}
              className="w-full bg-black text-white font-display font-black uppercase tracking-widest py-4 active:scale-95 transition-transform disabled:opacity-60"
            >
              {busy ? "Sending…" : "Send Reset Link"}
            </button>
            <button data-testid="cancel-forgot" onClick={() => { setMode("login"); setError(""); }} className="w-full font-display text-xs uppercase tracking-widest underline text-gray-500">
              Back to Login
            </button>
          </>
        )}
      </div>
    </div>
  );
};
